// KPI — Concentrazione prodotti (9), Composizione del fatturato (10), Cross/Up-selling (11), Margine stimato (16)
import React, { useState } from "react";
import { PieChart, Layers, Shuffle, Calculator } from "lucide-react";
import { useFm, fmtInt, fmtKg, Seg, Bars } from "../bi/biShared";
import { Kpi, Group, KpiGrid, RankList, SubTitle, fmtPct, fmtNum } from "./kpiShared";
import KpiProvinces from "./KpiProvinces";

const MIX = [{ value: "categories", label: "Prodotti" }, { value: "segments", label: "Clienti" }, { value: "provinces", label: "Geografico" }, { value: "agents", label: "Canale (agente)" }];

export default function KpiMix({ data, onReload }) {
  const fm = useFm();
  const F = data.formulas;
  const k = data.concentration, mx = data.mix, cr = data.cross, mg = data.margin;
  const [mix, setMix] = useState("categories");
  return (
    <div className="space-y-4" data-testid="kpi-tab-mix">
      <Group num={9} title="Concentrazione e rischio — prodotti" icon={PieChart} testId="kpi-g9p">
        <KpiGrid cols={3}>
          <Kpi label="Quota top 5 prodotti" value={fmtPct(k.top5_products_pct)} formula={F.top_prod} testId="kpi-conc-prod5" accent />
          <Kpi label="Quota top 10 prodotti" value={fmtPct(k.top10_products_pct)} formula={F.top_prod} testId="kpi-conc-prod10" />
          <Kpi label="Articoli venduti" value={fmtInt(k.products_total)} testId="kpi-conc-prodn" />
        </KpiGrid>
        <SubTitle>Combinazioni cliente–prodotto</SubTitle>
        <RankList rows={k.top_combos} label={(x) => x.customer} sub={(x) => x.description || x.code} value={(x) => fm(x.revenue)} right={(x) => `quota ${fmtPct(x.share_pct)}`} testIdPrefix="kpi-combo" />
      </Group>

      <Group num={10} title="Composizione del fatturato" icon={Layers} note={`${F.mix} ${data.notes.catalog} ${data.notes.geo}`} testId="kpi-g10">
        <Seg options={MIX} value={mix} onChange={setMix} testIdPrefix="kpi-mix" size="sm" wrap />
        {mix === "provinces" && <KpiProvinces onDone={onReload} />}
        <Bars data={mx[mix]} valueKey="share_pct" formatValue={(v) => fmtPct(v)} color="#1c4e8a" testIdPrefix={`kpi-mix-${mix}`} />
      </Group>

      <Group num={11} title="Cross-selling e up-selling" icon={Shuffle} note={data.notes.catalog} testId="kpi-g11">
        <KpiGrid cols={3}>
          <Kpi label="Tasso di cross-selling" value={fmtPct(cr.cross_rate_pct)} sub={`${fmtInt(cr.multi_customers)} su ${fmtInt(cr.active)} clienti con ≥2 categorie`} formula={F.cross} testId="kpi-cross-rate" accent />
          <Kpi label="Categorie medie per cliente" value={fmtNum(cr.avg_categories, 2)} formula={F.avg_cat} testId="kpi-cross-avg" />
          <Kpi label="Tasso di up-selling" value={fmtPct(cr.upsell_rate_pct)} sub={`${fmtInt(cr.upsell_customers)} su ${fmtInt(cr.eligible)} clienti in entrambi i periodi`} formula={F.upsell} testId="kpi-upsell" />
        </KpiGrid>
        <SubTitle>Clienti per numero di categorie acquistate</SubTitle>
        <Bars data={cr.distribution} valueKey="count" formatValue={(v) => `${fmtInt(v)} clienti`} testIdPrefix="kpi-cross-dist" />
      </Group>

      <Group num={16} title="Margine stimato (KPI che richiedono i costi)" icon={Calculator} note={data.notes.catalog} testId="kpi-g16">
        {mg.available ? (
          <>
            <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="kpi-margin-note">
              Stima: costo del venduto = kg venduti × costo di acquisto (acq) dell'articolo a catalogo. Copre il <b>{fmtPct(mg.coverage_pct)}</b> del fatturato ({fmtInt(mg.products_covered)} articoli con costo).
            </p>
            <KpiGrid>
              <Kpi label="Margine lordo stimato" value={fm(mg.margin)} formula={F.margin} testId="kpi-margin" accent />
              <Kpi label="Margine %" value={fmtPct(mg.margin_pct)} formula={F.margin_pct} testId="kpi-margin-pct" />
              <Kpi label="Fatturato con costo" value={fm(mg.revenue_covered)} sub={`su ${fm(mg.revenue_total)}`} testId="kpi-margin-rev" />
              <Kpi label="Costo del venduto stimato" value={fm(mg.cost)} testId="kpi-margin-cost" />
            </KpiGrid>
            <SubTitle>Articoli per margine stimato</SubTitle>
            <RankList rows={mg.top} label={(x) => x.description || x.code} sub={(x) => `${x.code} · ${fmtKg(x.kg, 1)} kg · costo ${fm(x.cost)}`} value={(x) => fm(x.margin)} right={(x) => `${fmtPct(x.margin_pct)} · ${fm(x.margin_kg)}/kg`} testIdPrefix="kpi-margin-top" />
          </>
        ) : (
          <p className="text-sm text-stone-500 italic py-4 text-center" data-testid="kpi-margin-empty">Nessun articolo del periodo ha un costo di acquisto a catalogo: margine non calcolabile.</p>
        )}
        <p className="text-[11px] text-stone-500" data-testid="kpi-margin-unavailable">Non calcolabili senza dati di costo esterni: Utile operativo, ROI, CAC, ROAS.</p>
      </Group>
    </div>
  );
}
